import { LitElement, PropertyValues, css, html, nothing } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import classNames from 'classnames';
import { getGlobalStyleSheets } from '../../global-styles';
import { ButtonEnums } from '../button';
import {
   FlexDirection,
   AlignItems,
   enumAttributeConverter,
   FlexDirectionValuesMap,
   AlignItemsValuesMap,
} from '../enum-types';
import { AtlToastMessageProps } from './props';
import {
   ToastVariant,
   ToastVariantIconsMap,
   ToastVariantValuesMap,
} from './types';
import { IconEnums } from '../icon';
import {
   ToastMessageActionEvent,
   ToastMessageCloseEvent,
} from './toast.events';

import * as TagNames from './toast.tagnames';

@customElement(TagNames.ToastMessage)
export class AtlToastMessage
   extends LitElement
   implements AtlToastMessageProps
{
   declare iconSource?: IconEnums.IconSource;
   declare iconSize?: IconEnums.IconSizes;
   declare iconSet?: IconEnums.IconSet;
   declare headerTitle?: string;
   declare headerTimeSpan?: string;
   declare bodyText?: string;
   declare bodyDirection?: FlexDirection;
   declare bodyAlignItems?: AlignItems;
   declare showBodyCloseButton?: boolean;
   declare variant?: ToastVariant;
   declare bodyCtaButtonLabel?: string;
   declare autoHide?: boolean;
   declare timeoutInSeconds?: number;
   declare showTimeoutProgressBar?: boolean;
   declare protected _show: boolean;

   declare _intervalId?: ReturnType<typeof setTimeout>;

   static properties = {
      iconSource: {
         type: String,
         converter: enumAttributeConverter(
            IconEnums.IconSource,
            IconEnums.IconSource.FontAwesome
         ),
      },
      iconSize: {
         type: String,
         converter: enumAttributeConverter(
            IconEnums.IconSizes,
            IconEnums.IconSizes.Medium
         ),
      },
      iconSet: {
         type: String,
         converter: enumAttributeConverter(
            IconEnums.IconSet,
            IconEnums.IconSet.Solid
         ),
      },
      headerTitle: { type: String },
      headerTimeSpan: { type: String },
      bodyText: { type: String },
      bodyDirection: {
         type: String,
         converter: enumAttributeConverter(FlexDirection, FlexDirection.Row),
      },
      bodyAlignItems: {
         type: String,
         converter: enumAttributeConverter(AlignItems, AlignItems.Start),
      },
      showBodyCloseButton: { type: Boolean },
      variant: {
         type: String,
         converter: enumAttributeConverter(ToastVariant, ToastVariant.Primary),
      },
      bodyCtaButtonLabel: { type: String },
      showTimeoutProgressBar: { type: Boolean },
      timeoutInSeconds: { type: Number },
      autoHide: { type: Boolean },
      _show: { type: Boolean, state: true },
   };

   static styles = [
      ...getGlobalStyleSheets(),
      css`
         @keyframes atltoastprogress {
            from {
               width: 0%;
            }
            to {
               width: 100%;
            }
         }
      `,
   ];

   constructor() {
      super();
      this._show = true;
   }

   @query('.progress')
   _progressBarContainer?: HTMLElement;

   @query('.progress-bar')
   _progressBar?: HTMLElement;

   private get _autoHide() {
      return this.autoHide || this.showTimeoutProgressBar || false;
   }

   connectedCallback() {
      super.connectedCallback();

      this._intervalId = setTimeout(() => {
         if (!this._autoHide) return;

         this._autoClose();
      }, this._timeoutInSeconds * 1000);

      this.addEventListener('mouseover', this._mouseOver);
      this.addEventListener('mouseout', this._mouseOut);
   }

   disconnectedCallback() {
      if (this._intervalId) {
         clearTimeout(this._intervalId);
      }

      this.removeEventListener('mouseout', this._mouseOut);
      this.removeEventListener('mouseover', this._mouseOver);

      super.disconnectedCallback();
   }

   private _mouseOver = () => {
      if (!this._autoHide) return;
      if (this._progressBar) {
         this._progressBar.style.animationPlayState = 'paused';
      }
   };

   private _mouseOut = () => {
      if (!this._autoHide) return;
      if (this._progressBar) {
         this._progressBar.style.animationPlayState = 'running';
      }
   };

   private _autoClose = () => {
      if (
         this._progressBarContainer?.clientWidth ===
         this._progressBar?.clientWidth
      ) {
         clearTimeout(this._intervalId);
         this._intervalId = undefined;
         this._onHideMessage();
         return;
      }

      this._intervalId = setTimeout(() => {
         this._autoClose();
      }, 1000);
   };

   get _timeoutInSeconds() {
      if (!this.timeoutInSeconds || this.timeoutInSeconds < 0) return 5;
      return this.timeoutInSeconds;
   }

   protected updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);

      if (this._progressBar) {
         this._progressBar.style.animation = `atltoastprogress ${this._timeoutInSeconds}s forwards`;
      }
   }

   renderCloseBtn() {
      return html`
         <button
            type="button"
            class="btn-close"
            data-bs-dismiss="toast"
            aria-label="Close"
            @click=${() => this._onHideMessage()}
         ></button>
      `;
   }

   renderHeader() {
      if (!this.headerTitle && !this.headerTimeSpan) return nothing;

      return html`
         <div part="header" class="toast-header">
            <div part="header-title" class="header-title">${this.headerTitle}</div>
            <div class="header-options">
               <span>${this.headerTimeSpan}</span>
               ${this.renderCloseBtn()}
            </div>
         </div>
      `;
   }

   renderBodyCtaButton() {
      if (!this.bodyCtaButtonLabel) return nothing;

      return html`
         <atlantis-button
            variant=${ButtonEnums.ButtonVariant.Cta}
            size=${ButtonEnums.ButtonSize.Small}
            @click=${() => this._bodyCtaClick()}
         >
            ${this.bodyCtaButtonLabel}
         </atlantis-button>
      `;
   }

   renderBodyCloseButton() {
      if (!this.showBodyCloseButton) return nothing;

      let buttonVariant: ButtonEnums.ButtonVariant;
      switch (this.variant) {
         case ToastVariant.Primary:
         case ToastVariant.Secondary:
         case ToastVariant.Success:
         case ToastVariant.Warning:
         case ToastVariant.Danger:
         case ToastVariant.Dark:
            buttonVariant = ButtonEnums.ButtonVariant.Light;
            break;
         default:
            buttonVariant = ButtonEnums.ButtonVariant.Secondary;
            break;
      }

      return html`
         <atlantis-button
            variant=${buttonVariant}
            size=${ButtonEnums.ButtonSize.Small}
            @click=${() => this._onHideMessage()}
         >
            Close
         </atlantis-button>
      `;
   }

   private _renderBodyIcon = () => {
      const iconName =
         ToastVariantIconsMap[this.variant ?? ToastVariant.PrimaryBordered];
      if (!iconName) return nothing;

      return html` <atlantis-icon
         name=${iconName}
         iconSource=${this.iconSource ?? IconEnums.IconSource.FontAwesome}
         iconSize=${this.iconSize ?? IconEnums.IconSizes.Large}
         iconSet=${this.iconSet ?? IconEnums.IconSet.Solid}
      ></atlantis-icon>`;
   };

   renderBody() {
      const bodyDirection =
         FlexDirectionValuesMap[this.bodyDirection ?? FlexDirection.Row];
      const bodyAlignItems =
         AlignItemsValuesMap[this.bodyAlignItems ?? AlignItems.Start];

      return html`
         <div part="body" class=${classNames('toast-body', bodyDirection, bodyAlignItems)}>
            ${this._renderBodyIcon()}
            <div part="body-message" class="body-message">${this.bodyText}</div>
            <div class="body-buttons">
               ${this.renderBodyCtaButton()} ${this.renderBodyCloseButton()}
            </div>
         </div>
      `;
   }

   renderTimeoutProgressBar() {
      if (!this.showTimeoutProgressBar) return nothing;

      return html`
         <div class="progress">
            <div
               class="progress-bar atl-toast-progress"
               role="progressbar"
               aria-label=${`${this.title} timeout progressbar`}
            ></div>
         </div>
      `;
   }

   render() {
      if (!this._show) return nothing;

      const variantClassName =
         ToastVariantValuesMap[this.variant ?? ToastVariant.Primary];

      return html`
         <div class="atl-wc-toast" part="toast-wrapper">
            <div
               part="toast"
               id="@Id"
               class=${classNames('toast', variantClassName, {
                  show: this._show,
               })}
               role="alert"
               aria-live="assertive"
               aria-atomic="true"
            >
               ${this.renderHeader()} ${this.renderBody()}
               ${this.renderTimeoutProgressBar()}
            </div>
         </div>
      `;
   }

   private _onHideMessage = () => {
      this._show = false;
      this.dispatchEvent(new ToastMessageCloseEvent());
   };

   private _bodyCtaClick = () => {
      this.dispatchEvent(new ToastMessageActionEvent());
   };
}
